import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import PubBadge from "../../components/PubBadge";
import { Star } from "lucide-react";
import { toast } from "sonner";

export default function ToDo() {
  const [tasks, setTasks] = useState(null);
  const [team, setTeam] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [saving, setSaving] = useState({});

  const load = useCallback(() => api.get("/admin/todo").then((r) => {
    setTasks(r.data.tasks || []);
    setTeam(r.data.team || []);
  }).catch(() => { setTasks([]); setTeam([]); }), []);

  useEffect(() => { load(); }, [load]);

  const filteredTasks = useMemo(
    () => (tasks || []).filter((t) => !statusFilter || t.status === statusFilter),
    [tasks, statusFilter],
  );

  const assign = async (task, assigneeId) => {
    const key = task.event_id + ":" + task.task_key;
    setSaving((current) => ({ ...current, [key]: true }));
    try {
      const { data } = await api.post(
        "/admin/events/" + task.event_id + "/production/" + task.task_key + "/assign",
        { assignee_id: assigneeId || null },
      );
      setTasks((current) => current.map((t) => t.id === task.id
        ? { ...t, assignee: data.assignee || null }
        : t));
      toast.success(assigneeId ? "Task assigned." : "Task unassigned.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update task assignment.");
    } finally {
      setSaving((current) => ({ ...current, [key]: false }));
    }
  };

  if (tasks === null) return <div className="p-8 text-sm text-[#5c5852]">Loading…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="todo-page">
      <div className="mb-6">
        <p className="btw-overline mb-1">Production</p>
        <h1 className="font-serif text-3xl">Tasks</h1>
        <p className="text-sm text-[#5c5852] mt-2">
          Production steps across submissions. Assign each step to an active team member and filter by progress.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4 mb-5">
        <label className="text-xs font-bold">
          Task status
          <select data-testid="todo-status-filter" className="block mt-1 border border-[#dcd3c6] bg-white px-3 py-2 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="to_do">To do</option>
            <option value="running">Running</option>
            <option value="error">Error</option>
            <option value="done">Done</option>
            <option value="not_configured">Not configured</option>
          </select>
        </label>
        <p className="text-xs text-[#5c5852]">{filteredTasks.length} task{filteredTasks.length === 1 ? "" : "s"}</p>
      </div>

      {filteredTasks.length === 0 && (
        <div className="bg-white border border-[#dcd3c6] p-6 text-sm text-[#5c5852] italic" data-testid="todo-empty">
          No tasks match this status.
        </div>
      )}

      <div className="space-y-3">
        {filteredTasks.map((task) => {
          const assignmentKey = task.event_id + ":" + task.task_key;
          return (
            <div key={task.id} className="bg-white border border-[#dcd3c6] p-4 flex flex-wrap items-center gap-4" data-testid={"todo-task-" + task.id}>
              {task.priority && <Star size={14} className="text-[#876a4f] fill-[#c19872]" aria-label="Priority submission" />}
              <span className="min-w-[220px] flex-1">
                <Link to={"/admin/events/" + task.event_id} className="font-medium hover:text-[#876a4f]">{task.event_name}</Link>
                <span className="block text-xs text-[#5c5852] mt-1">{task.task_label}</span>
              </span>
              <PubBadge pub={task.publication} />
              <span className={"text-xs px-3 py-1 rounded-full " + (task.status === "done" ? "bg-[#768c6a]/20 text-[#5c6e53]" : task.status === "error" ? "bg-[#b35c5c]/15 text-[#b35c5c]" : "bg-[#e2d3c0] text-[#5c5852]")}>
                {task.status.replaceAll("_", " ")}
              </span>
              {task.assignable ? (
                <label className="text-xs text-[#5c5852]">
                  Assigned to
                  <select
                    data-testid={"assign-task-" + task.id}
                    className="block mt-1 border border-[#dcd3c6] bg-white px-2 py-1.5 text-sm"
                    value={task.assignee?.id || ""}
                    disabled={Boolean(saving[assignmentKey]) || task.status === "done"}
                    onChange={(e) => assign(task, e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {team.map((member) => <option key={member.id} value={member.id}>{member.name || member.email}</option>)}
                  </select>
                </label>
              ) : task.assignee ? <span className="text-xs text-[#5c5852]">Assigned to {task.assignee.name || task.assignee.email}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}